export type AppErrorCode =
  | "FILE_EMPTY"
  | "FILE_TOO_BIG"
  | "FILE_UNSUPPORTED"
  | "PDF_WORKER"
  | "XLSX_PARSER"
  | "EDGE_400"
  | "EDGE_500"
  | "EDGE_NETWORK"
  | "AI_FAILED"
  | "RLS_DENY"
  | "DB_UPDATE"
  | "STORAGE_UPLOAD"
  | "AUTH"
  | "any";

export class AppError extends Error {
  code: AppErrorCode;
  details?: unknown;
  constructor(code: AppErrorCode, message: string, details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

export function toAppError(err: unknown, fallback: AppErrorCode = "any"): AppError {
  if (err instanceof AppError) return err;
  const e = err as { message?: string } | null | undefined;
  const msg = e?.message || String(e) || "Erro desconhecido";
  return new AppError(fallback, msg, e);
}

/**
 * Quando supabase.functions.invoke() retorna status não-2xx, o client preenche `error` com
 * mensagem genérica ("Edge Function returned no 2xx"). O body da resposta (ex: { error: "Pagamento não aprovado" })
 * costuma vir em `data`. Use este helper para obter a mensagem real do backend para exibir ao usuário.
 */
export function getInvokeErrorMessage(invokeError: unknown, responseData: unknown): string {
  if (
    responseData &&
    typeof responseData === "object" &&
    "error" in responseData &&
    typeof (responseData as { error?: string }).error === "string"
  ) {
    return (responseData as { error: string }).error;
  }
  const e = invokeError as { message?: string } | null | undefined;
  return e?.message || String(invokeError) || "Erro desconhecido";
}

/**
 * Converte erros técnicos de Edge Functions em mensagens amigáveis para o usuário.
 * Detecta erros como FunctionsHttpError e retorna uma mensagem genérica amigável.
 */
export function getFriendlyErrorMessage(error: unknown): string {
  if (!error) {
    return "Ops, algo errado aconteceu. Avise para nossa equipe.";
  }

  const errorObj = error as { message?: string; name?: string } | null | undefined;
  const errorMessage = errorObj?.message || String(error) || "";
  const errorName = errorObj?.name || "";
  
  // Detecta erros de Edge Functions (FunctionsHttpError ou mensagens relacionadas)
  const isEdgeFunctionError = 
    errorName.includes("FunctionsHttpError") ||
    errorName.includes("FunctionsFetchError") ||
    errorMessage.includes("Edge Function returned a non-2xx") ||
    errorMessage.includes("FunctionsHttpError") ||
    errorMessage.includes("FunctionsFetchError");

  if (isEdgeFunctionError) {
    return "Ops, algo errado aconteceu. Avise para nossa equipe.";
  }

  // Mantém mensagens amigáveis que já existem
  if (errorMessage.includes("Sessão expirada") || 
      errorMessage.includes("Não autorizado") ||
      errorMessage.includes("timeout") ||
      errorMessage.includes("conexão")) {
    return errorMessage;
  }

  // Para outros erros técnicos, retorna mensagem genérica
  // Mas mantém mensagens que parecem amigáveis (não contêm termos técnicos)
  const technicalTerms = [
    "Error:",
    "TypeError",
    "ReferenceError",
    "at ",
    "stack",
    "undefined",
    "null",
    "Failed to",
    "NetworkError",
    "FetchError"
  ];

  const seemsTechnical = technicalTerms.some(term => 
    errorMessage.toLowerCase().includes(term.toLowerCase())
  );

  if (seemsTechnical) {
    return "Ops, algo errado aconteceu. Avise para nossa equipe.";
  }

  // Se a mensagem parece amigável, retorna ela mesma
  return errorMessage;
}

/** Converte um AppError em mensagem amigável + dicas para o usuário */
export function userMessageFor(error: AppError): { title: string; tips: string[] } {
  switch (error.code) {
    case "FILE_EMPTY":
      return {
        title: "O arquivo está vazio ou inválido.",
        tips: ["Envie outro arquivo.", "Verifique se o arquivo abre normalmente no seu dispositivo."]
      };
    case "FILE_TOO_BIG":
      return {
        title: "Arquivo muito grande.",
        tips: ["Tamanho máximo permitido é 25 MB.", "Comprima ou divida o documento."]
      };
    case "FILE_UNSUPPORTED":
      return {
        title: "Formato de arquivo não suportado.",
        tips: ["Formatos aceitos: PDF, DOCX, XLSX, JPG, PNG."]
      };
    case "PDF_WORKER":
      return {
        title: "Falha ao abrir PDF.",
        tips: ["Tente outro PDF ou exporte como PDF/A.", "Se persistir, envie como imagem (JPG/PNG)."]
      };
    case "XLSX_PARSER":
      return {
        title: "Não foi possível ler a planilha.",
        tips: ["Salve a planilha novamente como .xlsx.", "Remova proteção/senha e tente de novo."]
      };
    case "EDGE_400":
      return {
        title: "Arquivo não pôde ser processado no servidor.",
        tips: ["Tente outro formato.", "Para PDF/XLSX, o app processa no navegador — reenvie o arquivo."]
      };
    case "EDGE_500":
      return {
        title: "Erro interno ao processar o arquivo.",
        tips: ["Tente novamente em instantes.", "Se continuar, mude o formato do arquivo."]
      };
    case "EDGE_NETWORK":
      return {
        title: "Conexão com o servidor instável.",
        tips: ["Verifique sua internet.", "Tente novamente."]
      };
    case "AI_FAILED":
      return {
        title: "A análise de IA falhou.",
        tips: ["Envie novamente.", "Se persistir, tente outro arquivo/qualidade."]
      };
    case "RLS_DENY":
      return {
        title: "Permissão negada para salvar os resultados.",
        tips: ["Refaça login.", "Verifique se o paciente foi criado por você."]
      };
    case "DB_UPDATE":
      return {
        title: "Não foi possível salvar os resultados.",
        tips: ["Tente novamente.", "Se persistir, entre em contato com o suporte."]
      };
    case "STORAGE_UPLOAD":
      return {
        title: "Falha ao enviar o arquivo.",
        tips: ["Verifique sua internet.", "Tente enviar novamente."]
      };
    default:
      return { title: "Não conseguimos processar este arquivo.", tips: ["Tente novamente.", "Envie em outro formato."] };
  }
}
